import { IDLE_SIGN_OUT_MS } from "@allergy123/auth-client";
import { useAuth, useIdleSignOut } from "@allergy123/auth-client/react";
import { Button, Card } from "@allergy123/ui";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useTimerHold } from "./encounter/RunningTimers";

const flushes = new Set<{ current: () => void }>();

/**
 * Lets a screen save input that is typed but not yet saved when the idle warning opens, a minute before an idle
 * sign-out would discard it (the encounter's draft, for one).
 */
export function useIdleFlush(flush: () => void) {
  const ref = useRef(flush);
  useEffect(() => {
    ref.current = flush;
  });
  useEffect(() => {
    flushes.add(ref);
    return () => {
      flushes.delete(ref);
    };
  }, []);
}

/**
 * Idle sign-out for the clinic (shared clinic computers): after IDLE_SIGN_OUT_MS without input in any tab of this
 * sign-in, hidden time included, the session ends through the auth client (screens removed, stored session cleared,
 * refresh token revoked, /auth/signed-out). A minute before, the warning covers the screen, so a record left open is
 * hidden while nobody is there; the record behind it is inert (no keyboard or screen-reader access) and unsaved input
 * is saved. "Stay signed in" (also Escape) keeps the session and puts the focus back where it was. A test timer this
 * tab started holds the sign-out until its last read is due plus a short grace (TIMER_GRACE_MS, capped), so staff are
 * not signed out under a running test. A tab whose stored clock already ran out (reopened, restored) never shows the
 * record at all.
 */
export function IdleSignOut({ children }: { children: ReactNode }) {
  const holdUntil = useTimerHold();
  const idle = useIdleSignOut({ holdUntil });
  const { signOut } = useAuth();
  const covered = idle.phase !== "active";
  // Children are not rendered while the stored clock says the session already ran out (a reopened or restored tab). Once
  // shown they stay mounted, inert, behind the cover, so a warning or a settling sign-out never empties a form.
  const [shown, setShown] = useState(idle.phase !== "expired");
  useEffect(() => {
    if (idle.phase !== "expired") setShown(true);
  }, [idle.phase]);

  // The warning saves what each screen has unsaved (useIdleFlush), once per warning.
  useEffect(() => {
    if (idle.phase === "warning") for (const f of flushes) f.current();
  }, [idle.phase]);

  // Focus goes back where it was in the screen when the warning is dismissed. Only focus inside the screen is
  // remembered: the warning's own button takes focus as it mounts, before any effect could look.
  const content = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && content.current?.contains(e.target)) lastFocus.current = e.target;
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);
  const wasCovered = useRef(false);
  useEffect(() => {
    if (covered) {
      wasCovered.current = true;
      return;
    }
    if (!wasCovered.current) return;
    wasCovered.current = false;
    if (lastFocus.current?.isConnected) lastFocus.current.focus({ preventScroll: true });
  }, [covered]);

  return (
    <>
      {/* Always the same wrapper, so a warning never remounts (and empties) the screen under it. */}
      <div ref={content} inert={covered}>
        {shown || idle.phase !== "expired" ? children : null}
      </div>
      {covered && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-canvas p-6">
          {idle.phase === "warning" ? (
            <Card
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="idle-title"
              aria-describedby="idle-body"
              className="w-full max-w-[34rem]"
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  idle.staySignedIn();
                }
              }}
            >
              <h2 id="idle-title" className="mb-2 text-h2 font-semibold text-anchor">
                Are you still there?
              </h2>
              <p id="idle-body" className="mb-6 text-ink-secondary">
                Nobody has used this tab for a while, so to protect patient information you'll be signed out in <span className="font-semibold tabular-nums text-anchor">{idle.secondsLeft}</span> {idle.secondsLeft === 1 ? "second" : "seconds"} (after {IDLE_SIGN_OUT_MS / 60_000} minutes without activity).
              </p>
              <div className="flex flex-wrap gap-2">
                <Button autoFocus onClick={idle.staySignedIn}>
                  Stay signed in
                </Button>
                <Button variant="secondary" surface="card" onClick={() => void signOut()}>
                  Sign out now
                </Button>
              </div>
            </Card>
          ) : (
            <p className="text-ink-secondary" role="status">
              Signing you out…
            </p>
          )}
        </div>
      )}
    </>
  );
}
